import { AxiosError } from 'axios';
import { dayjsPlugins, formatLocale } from '../providers/dayjs-plugins';
import { errorsTranslate } from './constants/errors';

export function formatDateTime(date: Date | string) {
  return formatLocale(date, 'DD/MM/YY HH:mm');
}

export function formatBRL(value: string | null | undefined) {
  let operator = '';

  if (!value) {
    return '';
  }

  operator = value.match(/[-]/g) ? '-' : '';

  value = value.replace(/\D/g, '');
  value = (Number(value) / 100).toFixed(2) + '';
  value = value.replace('.', ',');
  value = value.replace(/(\d)(\d{3})(\d{3}),/g, '$1.$2.$3,');
  value = value.replace(/(\d)(\d{3}),/g, '$1.$2,');

  return `${operator} R$${value}`;
}

export function realToUSCash(value: string) {
  if (!value) {
    return '';
  }

  return value
    .replace('R$', '')
    .replace('.', '')
    .replace(',', '.')
    .replace(' ', '');
}

export function translateServerHttpErrors(
  err: AxiosError<{ message: string }>,
  defaultMessage: string,
) {
  const serverMessage = err.response?.data.message;
  if (serverMessage) {
    return errorsTranslate?.[serverMessage] ?? serverMessage;
  }
  return defaultMessage;
}

export function getMinutesAndHours(minutes: number) {
  const roundedMinutes = Math.round(Math.abs(minutes));
  const restMinutes = roundedMinutes % 60;
  const hours = Math.floor(roundedMinutes / 60);

  return {
    hours,
    restMinutes,
  };
}

export function cryptData(data: { validator: string; template: string }) {
  return {
    cryptValidator: btoa(`${data.validator}/${new Date().getTime()}`),
    cryptTemplate: btoa(data.template),
  };
}

export function timeToSeconds(time: string) {
  const timeSplitList = time.split(':');
  const hoursSeconds = Number(timeSplitList[0]) * 3600;
  const minutesSeconds = Number(timeSplitList[1]) * 60;
  const seconds = Number(timeSplitList[2]);
  const totalInSeconds = hoursSeconds + minutesSeconds + seconds;

  return totalInSeconds;
}

export function secondsToTime(seconds: number) {
  const minutes = seconds / 60;
  const restMinutes = minutes % 60;
  const roundMinutes = Math.floor(restMinutes);
  const hours = (minutes - restMinutes) / 60;
  const restTimeToSeconds = roundMinutes * 60 + hours * 3600;

  const restSeconds = seconds - restTimeToSeconds;

  return {
    hours,
    minutes: roundMinutes,
    seconds: restSeconds,
    toString: () => `${hours}:${roundMinutes}:${restSeconds}`,
    toFormatedTime: () => `${hours}h ${roundMinutes}m ${restSeconds}s`,
  };
}

export const formatHours = (amount: number) => {
  const { hours, restMinutes } = getMinutesAndHours(Math.abs(amount));

  return `${hours.toString().padStart(2, '0')}h ${restMinutes
    .toString()
    .padStart(2, '0')}min`;
};

export const calculateDiffTotalHours = (date: Date[]) => {
  const totalMinutes = dayjsPlugins(date[0]).diff(date[1], 'minutes');

  return formatHours(totalMinutes);
};

export function normalizeText(text: string) {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(' ')
    .join('-')
    .toLowerCase();
}

/** Seconds -> "-1h 30min". Sign always in front, same format of the server. */
export const formatSignedHours = (seconds: number) => {
  const totalMinutes = Math.round(Math.abs(seconds) / 60);
  const sign = seconds < 0 && totalMinutes > 0 ? '-' : '';

  return `${sign}${Math.floor(totalMinutes / 60)}h ${String(
    totalMinutes % 60,
  ).padStart(2, '0')}min`;
};

const PAY_HOURS_REGEX = /^\d+:[0-5]\d$/;

/** "7:30" -> seconds. Empty = pay everything (undefined). Invalid = NaN. */
export function parsePayHours(value: string | undefined): number | undefined {
  const text = (value ?? '').trim();
  if (!text) return undefined;
  if (!PAY_HOURS_REGEX.test(text)) return NaN;

  const [hours, minutes] = text.split(':').map(Number);
  return hours * 3600 + minutes * 60;
}

/** Starts the browser download of a blob (ex: a generated PDF). */
export function downloadFile(response: Blob, fileName: string) {
  const url = URL.createObjectURL(new Blob([response]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
